/**
 * The on-device health card reader's files: tesseract.js's worker, its WebAssembly core and the
 * English model, all served from the patient app's own origin under /ocr/ (never a CDN).
 * vite.config.ts copies them out of node_modules at build time and serves them in development;
 * security-headers.ts gives /ocr/* the worker's own Content-Security-Policy. The service worker
 * never caches them (it keeps /assets/ and the shell list only), so a patient who never reads a
 * card never downloads them, and one who does gets them from the browser's HTTP cache next time.
 *
 * Plain TypeScript with no imports, so the browser bundle and the Node build share one list.
 */

/** The served directory, with its trailing slash: the engine's corePath. */
export const HEALTH_CARD_READER_BASE = "/ocr/";

/** The worker script tesseract.js starts (a classic worker, from this origin, not a blob:). */
export const HEALTH_CARD_READER_WORKER = "worker.min.js";

/** The English model: tesseract.js's LSTM-only data (4.0.0_best_int), gzipped. */
export const HEALTH_CARD_READER_LANGUAGE = "eng";

export interface HealthCardReaderAsset {
  /** The file name under HEALTH_CARD_READER_BASE. */
  readonly name: string;
  /** The npm package it is copied from. */
  readonly packageName: "tesseract.js" | "tesseract.js-core" | "@tesseract.js-data/eng";
  /** Its path inside that package. */
  readonly path: string;
}

/**
 * Every file served under /ocr/. The worker picks one core by what the device's WebAssembly
 * supports (relaxed SIMD, SIMD, or neither), and only the LSTM-only builds are shipped.
 */
export const HEALTH_CARD_READER_ASSETS: readonly HealthCardReaderAsset[] = [
  { name: HEALTH_CARD_READER_WORKER, packageName: "tesseract.js", path: "dist/worker.min.js" },
  {
    name: "worker.min.js.LICENSE.txt",
    packageName: "tesseract.js",
    path: "dist/worker.min.js.LICENSE.txt",
  },
  { name: "tesseract.js.LICENSE.md", packageName: "tesseract.js", path: "LICENSE.md" },
  ...[
    "tesseract-core-lstm.wasm.js",
    "tesseract-core-simd-lstm.wasm.js",
    "tesseract-core-relaxedsimd-lstm.wasm.js",
  ].map((name) => ({ name, packageName: "tesseract.js-core" as const, path: name })),
  { name: "tesseract.js-core.LICENSE.txt", packageName: "tesseract.js-core", path: "LICENSE" },
  {
    name: `${HEALTH_CARD_READER_LANGUAGE}.traineddata.gz`,
    packageName: "@tesseract.js-data/eng",
    path: `4.0.0_best_int/${HEALTH_CARD_READER_LANGUAGE}.traineddata.gz`,
  },
];
